/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  MessageCallout,
  MessageVariety,
  toast,
} from '@sonarsource/echoes-react';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useJiraWorkTypes } from '../../hooks/useJiraWorkTypes';
import { JiraProjectWorkTypesGroup } from './JiraProjectWorkTypesGroup';

interface JiraWorkTypesFormProps {
  jiraProjectKey: string;
  sonarOrganizationUuid: string;
  sonarProjectId: string;
}

export function JiraProjectWorkTypesForm({
  jiraProjectKey,
  sonarOrganizationUuid,
  sonarProjectId,
}: Readonly<JiraWorkTypesFormProps>) {
  const {
    loadingWorkTypes,
    savingWorkTypes,
    workTypes,
    hasSelectionChanged,
    reset,
    setSelectedWorkTypeIds,
    selectedWorkTypeIds,
    someWorkTypeHasMandatoryFields,
    saveWorkTypes,
  } = useJiraWorkTypes({
    jiraProjectKey,
    mutationOptions: {
      onSuccess: () => {
        toast.success({
          description: (
            <FormattedMessage id="project_settings.jira.binding.work_types.saved_successfully" />
          ),
          duration: 'short',
        });
      },
      onError: () => {
        toast.error({
          description: (
            <FormattedMessage id="project_settings.jira.binding.work_types.save_failed" />
          ),
          duration: 'short',
        });
      },
    },
    sonarOrganizationUuid,
    sonarProjectId,
  });

  const [showNoWorkTypeSelectedAlert, setShowNoWorkTypeSelectedAlert] = useState(false);

  const handleSaveWorkTypes = () => {
    if (isEmpty(selectedWorkTypeIds)) {
      setShowNoWorkTypeSelectedAlert(true);
    } else {
      void saveWorkTypes();
    }
  };

  const handleChange = (selectedWorkTypeIds: string[]) => {
    setSelectedWorkTypeIds(selectedWorkTypeIds);
    setShowNoWorkTypeSelectedAlert(false);
  };

  const handleCancel = () => {
    reset();
    setShowNoWorkTypeSelectedAlert(false);
  };

  return (
    <>
      <JiraProjectWorkTypesGroup
        hasMandatoryFields={someWorkTypeHasMandatoryFields}
        isLoading={loadingWorkTypes}
        onChange={handleChange}
        selectedWorkTypeIds={selectedWorkTypeIds}
        workTypes={workTypes}
      />
      {showNoWorkTypeSelectedAlert && (
        <MessageCallout className="sw-mt-2" variety={MessageVariety.Danger}>
          <FormattedMessage id="project_settings.jira_binding.work_types.at_least_one_required" />
        </MessageCallout>
      )}
      {hasSelectionChanged && (
        <ButtonGroup className="sw-mt-3">
          <Button
            isDisabled={savingWorkTypes || showNoWorkTypeSelectedAlert}
            isLoading={savingWorkTypes}
            onClick={handleSaveWorkTypes}
            variety={ButtonVariety.Primary}
          >
            <FormattedMessage id="save" />
          </Button>
          <Button onClick={handleCancel}>
            <FormattedMessage id="cancel" />
          </Button>
        </ButtonGroup>
      )}
    </>
  );
}
